// src/lib/firebase/firebaseClient.ts
"use client";

import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, onMessage, Messaging } from "firebase/messaging";

// ----------------------------------------------------------------------------------
// Load Firebase Config from .env (public vars only)
// ----------------------------------------------------------------------------------
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY!,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN!,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID!,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET!,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID!,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID!,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID!,
};

// Public VAPID key (Web Push)
const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY!;

// Canonical SW path (DO NOT change casually)
const SW_PATH = "/sw.js";

// ----------------------------------------------------------------------------------
// Firebase Initialization (client-only + single-init protection)
// ----------------------------------------------------------------------------------
let messaging: Messaging | null = null;

export function initFirebase() {
  if (typeof window === "undefined") return; // SSR guard

  if (!getApps().length) {
    initializeApp(firebaseConfig);
  }

  try {
    messaging = getMessaging();
  } catch {
    messaging = null;
  }
}

export { messaging };

// ----------------------------------------------------------------------------------
// Safe SW registration helper
// - Reuses canonical /sw.js registration
// - Removes legacy/conflicting registrations (e.g. firebase-messaging-sw.js)
// ----------------------------------------------------------------------------------
async function getOrRegisterCanonicalServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;

  // Wait until browser has loaded previous registrations
  await navigator.serviceWorker.ready.catch(() => undefined);

  const regs = await navigator.serviceWorker.getRegistrations();

  // Try to find existing canonical /sw.js registration
  const canonical = regs.find((reg) => {
    const scriptUrl =
      reg.active?.scriptURL || reg.waiting?.scriptURL || reg.installing?.scriptURL || "";
    return scriptUrl.includes(SW_PATH);
  });

  if (canonical) {
    return canonical;
  }

  // Remove legacy/conflicting SW registrations (safe cleanup)
  // Example: /firebase-messaging-sw.js or old custom paths
  await Promise.all(
    regs.map(async (reg) => {
      const scriptUrl =
        reg.active?.scriptURL || reg.waiting?.scriptURL || reg.installing?.scriptURL || "";

      // Keep only unknown empty registrations; remove non-canonical ones
      if (scriptUrl && !scriptUrl.includes(SW_PATH)) {
        try {
          await reg.unregister();
        } catch {
          // no-op
        }
      }
    })
  );

  // Register canonical SW with explicit root scope
  return navigator.serviceWorker.register(SW_PATH, { scope: "/" });
}

// ----------------------------------------------------------------------------------
// Request FCM Web Push Token
// - Uses canonical /sw.js only
// ----------------------------------------------------------------------------------
export async function requestFcmToken(): Promise<string | null> {
  if (!messaging) return null;
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;

  try {
    const swRegistration = await getOrRegisterCanonicalServiceWorker();
    if (!swRegistration) return null;

    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    });

    return token || null;
  } catch (err) {
    // Optional debug:
    // console.error("FCM token error:", err);
    return null;
  }
}

// ----------------------------------------------------------------------------------
// Foreground Push Notification Handler
// ----------------------------------------------------------------------------------
export function onForegroundMessage(callback: (payload: any) => void) {
  if (!messaging) return;

  onMessage(messaging, (payload) => {
    callback(payload);
  });
}

// DEBUG ONLY – expose to browser console
if (typeof window !== "undefined") {
  // @ts-ignore
  window.requestFcmToken = requestFcmToken;
}